import { $isListNode } from "@lexical/list";
import {
  $getRoot,
  $isDecoratorNode,
  $isElementNode,
  $isParagraphNode,
  $isTabNode,
  $isTextNode,
  DecoratorNode,
  ElementNode,
} from "lexical";
import { notEmpty } from "../../../../../utils/not-empty";
import { ButtonListNode } from "../../nodes/button-list-node";
import { $isDesignHuddleNode, DefaultProportionOfMaxWidth } from "../../nodes/design-huddle-node";
import { $isDynamicContentNode } from "../../nodes/dynamic-content/dynamic-content-node";
import { $isDynamicContentToolbarNode } from "../../nodes/dynamic-content/helpers";
import { $isImageNode } from "../../nodes/image-node";
import { $isMergeTagV2Node } from "../../nodes/merge-tag-node-v2";
import { $isPollNode } from "../../nodes/poll/helpers";
import { $isSurveyAnswersNode } from "../../nodes/survey-answers/helpers";
import { $isVideoNode } from "../../nodes/video-node";
import { $convertToMarkdownString } from "../convert-to-markdown-string";
import { $findAllNodesOfType } from "../find-all-nodes-of-type";
import { getNewImageUrl } from "../get-new-image-url";
import { makePollUrl } from "../make-poll-url";
import {
  convertSlackRichTextList,
  convertSlackRichTextParagraph,
  createSlackRichText,
} from "../markdown-transformers/slack-rich-text-transform";

import type { UUID } from "../../../../../utils/uuid";
import type { PollWithOptions } from "../../../../poll/server/supabase-poll-service";
import type { DesignHuddleNode } from "../../nodes/design-huddle-node";
import type { ImageNode } from "../../nodes/image-node";
import type { PollNode } from "../../nodes/poll/poll-node";
import type { VideoNode } from "../../nodes/video-node";
import type { MardownBlock, ParagraphBlock } from "../../types/markdown-node-blocks";
import type { Transformer } from "@lexical/markdown";
import type { LexicalEditor } from "lexical";
import type { ContextBlock } from "slack-blocks-to-jsx";

export function chatBlockJson(
  editor: LexicalEditor,
  transformers: Transformer[],
  channel: "slack" | "teams",
  imageResize: { maxWidth: number },
  accountId: UUID,
  pollsData: { [key: string]: PollWithOptions } = {},
  teamsPollCards = false,
): { text: string; body: string } {
  const imageNodes = getImageNodes(editor);
  const designHuddleNodes = getDesignHuddleNodes(editor);
  const videoNodes = getVideoNodes(editor);

  const updatedData = updatedUrls(imageNodes, designHuddleNodes, videoNodes, channel, imageResize);

  editor.update(() => {
    updatedData.forEach(({ node, url }) => {
      node.setNewUrl(url);
    });
  });

  const text = getPlainText(editor);
  const blocks = createMarkdownBlocks(
    editor,
    transformers,
    channel,
    accountId,
    pollsData,
    teamsPollCards,
  );

  return {
    body: JSON.stringify({ blocks }),
    text,
  };
}

function getImageNodes(editor: LexicalEditor): ImageNode[] {
  // Not filtered to our own images: getNewImageUrl leaves anything it cannot resize alone.
  return editor.getEditorState().read(() => $findAllNodesOfType($getRoot(), $isImageNode));
}

function getVideoNodes(editor: LexicalEditor): VideoNode[] {
  return editor.getEditorState().read(() => $findAllNodesOfType($getRoot(), $isVideoNode));
}

function getDesignHuddleNodes(editor: LexicalEditor): DesignHuddleNode[] {
  return editor.getEditorState().read(() => $findAllNodesOfType($getRoot(), $isDesignHuddleNode));
}

function updatedUrls(
  imageNodes: ImageNode[],
  designHuddleNodes: DesignHuddleNode[],
  videoNodes: VideoNode[],
  channel: "slack" | "teams",
  imageResize: { maxWidth: number },
): { node: ImageNode | DesignHuddleNode | VideoNode; url: string }[] {
  const imageUpdates = imageNodes.map((node) => getNewImageUrl(node, imageResize));

  const designHuddleUpdates = designHuddleNodes.map((node) => {
    const previewUrl = node.getDesignHuddlePreviewUrl(imageResize.maxWidth);
    if (previewUrl) return { node, url: previewUrl };

    if (channel === "slack" && node.__proportionOfMaxWidth < DefaultProportionOfMaxWidth) {
      return getNewImageUrl(node, imageResize);
    }

    return { node, url: node.getImageUrl() };
  });

  const videoUpdates = videoNodes.map((node) => getNewImageUrl(node, imageResize));

  return [...imageUpdates, ...designHuddleUpdates, ...videoUpdates];
}

function getPlainText(editor: LexicalEditor): string {
  return editor.getEditorState().read(() => $getRoot().getTextContent());
}

function createMarkdownBlocks(
  editor: LexicalEditor,
  transformers: Transformer[],
  channel: "slack" | "teams",
  accountId: UUID,
  pollsData: { [key: string]: PollWithOptions } = {},
  teamsPollCards = false,
): MardownBlock[] {
  const topLevelNodes = editor.getEditorState().read(() =>
    $getRoot()
      .getChildren()
      .filter((c) => c instanceof ElementNode || c instanceof DecoratorNode),
  );

  const generateBlocks = (
    elements: (ElementNode | DecoratorNode<unknown>)[],
    dynamicCondition?: string,
  ): MardownBlock[] => {
    const blocks: MardownBlock[] = [];

    elements.forEach((element) => {
      const root = $getRoot();
      root.clear();

      if (channel === "slack" && $isElementNode(element)) {
        sanitizeSlackFormatting(element);
      }

      if ($isDynamicContentToolbarNode(element)) return;

      if ($isDynamicContentNode(element)) {
        const children = element.getChildren();
        const goodChildren = children.filter((n) => $isElementNode(n) || $isDecoratorNode(n));
        const nestedBlocks = generateBlocks(goodChildren, element.getCleanSegmentId());
        blocks.push(...nestedBlocks);
      } else if ($isPollNode(element)) {
        const pollBlocks = handlePoll(element, pollsData, channel, accountId, teamsPollCards);
        blocks.push(
          ...pollBlocks.map((block) => ({ ...block, dynamic_condition: dynamicCondition })),
        );
      } else if ($isSurveyAnswersNode(element)) {
        const surveyId = element.getSurveyId();
        blocks.push({
          type: "survey_answers",
          survey_id: surveyId,
          mail_merge_key: `survey_result_${surveyId}`,
          question_bold: element.getQuestionBold(),
          question_italic: element.getQuestionItalic(),
          answer_bold: element.getAnswerBold(),
          answer_italic: element.getAnswerItalic(),
          dynamic_condition: dynamicCondition,
        });
      } else if (channel === "slack" && $isParagraphNode(element) && !element.isEmpty()) {
        const sectionBlock = convertSlackRichTextParagraph(element);
        if (sectionBlock) {
          blocks.push({ ...sectionBlock, dynamic_condition: dynamicCondition });
        }
      } else if (channel === "slack" && $isParagraphNode(element) && element.isEmpty()) {
        // Special case for empty paragraphs, which come through as a paragraph node with no children.
        // Slack does a good job of removing whitespace rich_text elements
        blocks.push({ ...createSlackRichText(), dynamic_condition: dynamicCondition });
      } else if (channel === "slack" && $isListNode(element)) {
        const listBlocks = convertSlackRichTextList(element);
        listBlocks.forEach((block) =>
          blocks.push({ ...block, dynamic_condition: dynamicCondition }),
        );
      } else {
        root.append(element);
        const markdown = $convertToMarkdownString(transformers);
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        const parsedBlock = JSON.parse(markdown) as MardownBlock;
        blocks.push({ ...parsedBlock, dynamic_condition: dynamicCondition });
      }
    });

    return blocks;
  };

  let markdownBlocks: MardownBlock[] = [];
  editor.update(
    () => {
      markdownBlocks = generateBlocks(topLevelNodes);
    },
    { discrete: true },
  );

  return markdownBlocks;
}

function sanitizeSlackFormatting(element: ElementNode): void {
  element.getChildren().forEach((child) => {
    if ($isMergeTagV2Node(child) || $isTabNode(child) || !$isTextNode(child)) return;

    const updatedText = child
      .getTextContent()
      .replace(/(?<!\u200B)\*(?!\u200B)/g, "﹡")
      .replace(/(?<!\u200B)_(?!\u200B)/g, "ˍ");

    child.setTextContent(updatedText);
  });
}

function handlePoll(
  element: PollNode,
  pollsData: { [key: string]: PollWithOptions },
  channel: "slack" | "teams",
  accountId: UUID,
  teamsPollCards: boolean,
): MardownBlock[] {
  const pollId = element.getPollId();
  if (!pollId || !pollsData[pollId]) return [];

  const { poll } = pollsData[pollId];

  // Spike (CHA-4579): teams-engine-v2 renders these as Action.Submit buttons rather than links, so
  // link scanners can't answer the poll. The url is only carried for the callback to record.
  if (channel === "teams" && teamsPollCards) {
    return [
      { type: "paragraph" as const, text: `**${poll.question}**` },
      {
        type: "poll_buttons" as const,
        poll_id: pollId,
        options: poll.options.map((option) => ({
          id: option.id,
          text: option.text,
          url: makePollUrl(accountId, option.id),
        })),
      },
      poll.helper_text
        ? ({ type: "paragraph" as const, text: poll.helper_text } as ParagraphBlock)
        : null,
    ].filter(notEmpty);
  }

  const buttonList = ButtonListNode.makeChatBlock(
    poll.options.map((option) => ({
      id: option.id,
      text: option.text,
      url: makePollUrl(accountId, option.id),
    })),
  );

  if (channel === "slack") {
    return [
      createSlackRichText(poll.question, { bold: true, italic: false }),
      buttonList,
      poll.helper_text
        ? ({
            type: "context",
            elements: [{ type: "mrkdwn", text: poll.helper_text }],
          } as ContextBlock)
        : null,
    ].filter(notEmpty);
  }

  return [
    { type: "paragraph" as const, text: `**${poll.question}**` },
    buttonList,
    poll.helper_text
      ? ({ type: "paragraph" as const, text: poll.helper_text } as ParagraphBlock)
      : null,
  ].filter(notEmpty);
}
